<script setup lang="ts">
import { onMounted, PropType, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { Participant, Project } from "../../../types/PersologOnlineTypes.ts";
import { isMobileView } from "../../../utils/GeneralUtils.ts";
import { getProductNames } from "../../../utils/ProjectUtils.ts";

const { t } = useI18n();

const props = defineProps({
    id: {
        type: String,
        required: true,
    },
    project: {
        type: Object as () => Project,
        required: true,
    },
    participants: {
        type: Array as PropType<Participant[]>,
        default: () => [],
    },
    existingParticipants: {
        type: Array as PropType<Participant[]>,
        default: () => [],
    },
});

const allParticipants = ref<Participant[]>([]);

const emit = defineEmits(["update:valid", "update:participants"]);

const participants = ref<Participant[]>(props.participants ?? []);
// Starts empty rather than undefined: the product column renders on the first
// paint, before getProductNames() resolves, and indexing undefined throws.
const productNames = ref<Record<string, string>>({});

function getParticipantIndexByEmail(email: string) {
    return participants.value.findIndex((p) => p.email === email);
}

function removeParticipant(participant: Participant) {
    const index = getParticipantIndexByEmail(participant.email);
    if (index !== undefined && index !== -1) {
        participants.value.splice(index, 1);
        allParticipants.value = [...participants.value, ...props.existingParticipants];
        emit("update:participants", participants.value);
    }
}

function clearAllParticipants() {
    participants.value = participants.value.filter((p) => p.id !== undefined);
    allParticipants.value = [...participants.value, ...props.existingParticipants];
    emit("update:participants", participants.value);
}

watch(
    () => props.participants,
    (newVal) => {
        participants.value = newVal;
        if (participants.value.length > 0) {
            allParticipants.value = [...participants.value, ...props.existingParticipants];
        }
    },
    { immediate: true },
);

onMounted(async () => {
    productNames.value = await getProductNames();
    allParticipants.value = [...props.participants, ...props.existingParticipants];
});
</script>

<template>
    <div class="flex flex-wrap gap-4 mt-4 mb-8" :id="`participant-table_div_participants-${id}`">
        <DataView
            v-if="isMobileView()"
            :ref="`participant-table_data-view_${id}`"
            :value="allParticipants"
            class="w-full"
            data-key="id"
            :pt="{
                header: {
                    style: {
                        'margin-bottom': '0 !important',
                    },
                },
                content: {
                    style: {
                        'border-bottom': '1px solid var(--p-divider-border-color)',
                        'border-top': '1px solid var(--p-divider-border-color)',
                        'background-color': 'white !important',
                    },
                },
            }"
        >
            <template #header>
                <span class="font-bold">{{ t("project-configuration.participants") }}</span>
            </template>
            <template #empty>
                <span>{{ t("project-details.participants.columns.empty") }}</span>
            </template>
            <template #list="slotProps">
                <div v-for="(participant, index) in slotProps.items" :key="participant.id">
                    <div class="ml-2 my-4 flex flex-wrap gap-4">
                        <div class="grid grid-cols-12 gap-4 w-full">
                            <div v-if="participant.firstName !== '' || participant.name !== ''" class="font-bold col-span-6">
                                {{ participant.firstName }} {{ participant.name }}
                            </div>
                            <div v-if="participant.firstName === '' && participant.name === ''" class="font-bold col-span-6">
                                {{ participant.email }}
                            </div>
                            <div class="col-span-6 flex justify-end">
                                <Button icon="pi pi-trash" @click="removeParticipant(participant)" :aria-label="t('accessibility.buttons.remove')" />
                            </div>
                        </div>
                        <div v-if="participant.firstName !== '' && participant.name !== ''" class="grid grid-cols-12 gap-4">
                            <div class="col-span-12">
                                {{ participant.email }}
                            </div>
                        </div>
                    </div>
                    <Divider v-if="Number(index) + 1 < participants.length" class="mr-4" />
                </div>
            </template>
        </DataView>
        <DataTable v-else :ref="`participant-table_data-table_${id}`" :value="allParticipants" class="w-full">
            <Column field="email" :header="t('project-configuration.additionalRecipient.email')"></Column>
            <Column field="firstName" :header="t('project-configuration.additionalRecipient.firstName')"></Column>
            <Column field="name" :header="t('project-configuration.additionalRecipient.lastName')"></Column>
            <Column
                v-if="project && project.defaultProduct && project.product != project.defaultProduct"
                field="selectedProduct"
            >
                <template #body="slotProps">
                    {{ productNames[slotProps.data.selectedProduct] ?? slotProps.data.selectedProduct }}
                </template>
            </Column>
            <Column field="action" :header="t('project-details.participants.columns.actions')">
                <template #body="slotProps">
                    <Button
                        :id="'participant-table_delete_' + getParticipantIndexByEmail(slotProps.data.email)"
                        :disabled="slotProps.data.id !== undefined"
                        icon="pi pi-trash"
                        @click="removeParticipant(slotProps.data)"
                        :aria-label="t('accessibility.buttons.remove')"
                    />
                </template>
            </Column>
        </DataTable>
        <div v-if="participants?.length !== 0" class="w-full flex justify-end">
            <Button size="small" link @click="clearAllParticipants" :aria-label="t('accessibility.buttons.clearAll')">{{ t("project-configuration.clearAll") }}</Button>
        </div>
    </div>
</template>
